import { useMutation, useQuery } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

const staleTime = 180000; //3 minuti

const fetchFolders = () => Axios.get('/folders');

const prefetchFolders = async () => {
    await queryClient.prefetchQuery({
        queryKey: ['folders'],
        queryFn: fetchFolders,
        staleTime: staleTime,
        cacheTime: Infinity,
        retry: false,
    });
};

const useFolders = () => {
    return useQuery({
        queryKey: ['folders'],
        queryFn: fetchFolders,
        staleTime: staleTime,
        cacheTime: Infinity,
        retry: false,
    });
};

const forceReloadFolders = () => queryClient.invalidateQueries({ queryKey: ['folders'] });

const createFolder = (folder) => Axios.post('/folders', folder);

const useCreateFolder = () => {
    return useMutation({
        mutationFn: createFolder,
        onSuccess: () => forceReloadFolders(),
    });
};

//la PUT aggiorna solo i metadati: i contatti si gestiscono con gli endpoint dedicati
const updateFolder = ({ folderID, folder }) => Axios.put(`/folders/${folderID}`, folder);

const useUpdateFolder = () => {
    return useMutation({
        mutationFn: updateFolder,
        onSuccess: () => forceReloadFolders(),
    });
};

const deleteFolder = (folderID) => Axios.delete(`/folders/${folderID}`);

const useDeleteFolder = () => {
    return useMutation({
        mutationFn: deleteFolder,
        onSuccess: () => forceReloadFolders(),
    });
};

const addContactsToFolder = ({ folderID, contactIDs }) =>
    Axios.post(`/folders/${folderID}/contacts`, { ContactIDs: contactIDs });

const useAddContactsToFolder = () => {
    return useMutation({
        mutationFn: addContactsToFolder,
        onSuccess: () => forceReloadFolders(),
    });
};

//la DELETE con body richiede la config `data`, non il secondo parametro come la POST
const removeContactsFromFolder = ({ folderID, contactIDs }) =>
    Axios.delete(`/folders/${folderID}/contacts`, { data: { ContactIDs: contactIDs } });

const useRemoveContactsFromFolder = () => {
    return useMutation({
        mutationFn: removeContactsFromFolder,
        onSuccess: () => forceReloadFolders(),
    });
};

const orderFolderContacts = ({ folderID, contactIDs }) =>
    Axios.put(`/folders/${folderID}/contacts/order`, { ContactIDs: contactIDs });

const useOrderFolderContacts = () => {
    return useMutation({
        mutationFn: orderFolderContacts,
        //il nuovo ordine e' gia' a schermo: si scrive in cache per non far tornare indietro le righe
        onMutate: ({ folderID, contactIDs }) => {
            queryClient.setQueryData(['folders'], (old) => {
                if (!Array.isArray(old)) {
                    return old;
                }
                return old.map((folder) =>
                    folder.ID === folderID ? { ...folder, ContactIDs: [...contactIDs] } : folder
                );
            });
        },
        onSuccess: () => forceReloadFolders(),
    });
};

export {
    prefetchFolders,
    useFolders,
    forceReloadFolders,
    useCreateFolder,
    useUpdateFolder,
    useDeleteFolder,
    useAddContactsToFolder,
    useRemoveContactsFromFolder,
    useOrderFolderContacts,
};
